import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";

import { mapOnlineState } from "../mapper/common";
import type {
  Channel,
  Device,
  DeviceChannels,
  DeviceState,
  DeviceStateChannels,
  FumeHoodStateMessage,
  OperationMode,
} from "../view-model";

interface DevicesStore {
  // State
  devices: { [id: string]: Device };
  loadingStates: {
    devices: boolean;
    onlineState: boolean;
    operationState: boolean;
    infoMessages: boolean;
  };
  errors: {
    devices?: string;
    onlineState?: string;
    operationState?: string;
    infoMessages?: string;
  };
  lastUpdated: {
    devices: number;
    onlineState: number;
    operationState: number;
    infoMessages: number;
  };

  // Actions
  setDevices: (devices: Device[]) => void;
  updateDevice: (deviceId: string, updates: Partial<Device>) => void;
  removeDevice: (deviceId: string) => void;
  updateDeviceIncidentStates: (deviceId: string, incidentStates: any) => void;
  updateDeviceChannel: (
    deviceId: string,
    channelId: string,
    value: any
  ) => void;
  updateDeviceStateChannel: (
    deviceId: string,
    channelId: string,
    value: any
  ) => void;
  updateDeviceChannels: (deviceId: string, channels: DeviceChannels) => void;
  updateDeviceStateChannels: (
    deviceId: string,
    stateChannels: DeviceStateChannels
  ) => void;
  setLoading: (
    key: keyof DevicesStore["loadingStates"],
    loading: boolean
  ) => void;
  setError: (key: keyof DevicesStore["errors"], error: string | null) => void;
  clearErrors: () => void;

  // Selectors
  getDevice: (id: string) => Device | undefined;
  getDevices: () => Device[];
  getDevicesByClass: (className: string) => Device[];
  getDevicesByRoom: (roomId: string) => Device[];
  getCalculatedDeviceState: (deviceId: string) => DeviceState | null;
  getCalculatedFumeHoodState: (deviceId: string) => Device["state"] | null;
  getDeviceMessages: (deviceId: string) => FumeHoodStateMessage[];
  getDeviceOperationMode: (deviceId: string) => OperationMode | undefined;
  getDeviceStatus: (
    deviceId: string
  ) => "online" | "offline" | "error" | "initialization" | undefined;
}

// Helper function to add fumehood state message from channel
const addFumehoodStateMessageFromChannel = (
  messages: FumeHoodStateMessage[],
  infoStateChannel: Channel | undefined
): void => {
  if (infoStateChannel?.current?.value === 1) {
    messages.push({
      name: infoStateChannel.name,
      code: infoStateChannel.id,
      message: `state.message.${infoStateChannel.name}`,
      severity: "info",
    });
  }
};

export const useDevicesStore = create<DevicesStore>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      // Initial state
      devices: {},
      loadingStates: {
        devices: false,
        onlineState: false,
        operationState: false,
        infoMessages: false,
      },
      errors: {},
      lastUpdated: {
        devices: 0,
        onlineState: 0,
        operationState: 0,
        infoMessages: 0,
      },

      // Actions
      setDevices: (devices: Device[]) => {
        const deviceMap: { [id: string]: Device } = {};
        for (const device of devices) {
          deviceMap[device.id] = device;
        }

        set({
          devices: deviceMap,
          lastUpdated: { ...get().lastUpdated, devices: Date.now() },
          loadingStates: { ...get().loadingStates, devices: false },
          errors: { ...get().errors, devices: undefined },
        });
      },

      updateDevice: (deviceId: string, updates: Partial<Device>) => {
        set((state) => {
          const existing = state.devices[deviceId];
          if (existing) {
            return {
              devices: {
                ...state.devices,
                [deviceId]: { ...existing, ...updates },
              },
            };
          }
          // Device not in the store (e.g. just removed from a room)
          if (updates.id) {
            return {
              devices: { ...state.devices, [deviceId]: updates as Device },
            };
          }
          return state;
        });
      },

      removeDevice: (deviceId: string) => {
        set((state) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [deviceId]: _, ...remaining } = state.devices;
          return {
            devices: remaining,
            lastUpdated: { ...state.lastUpdated, devices: Date.now() },
          };
        });
      },

      updateDeviceIncidentStates: (deviceId: string, incidentStates: any) => {
        set((state) => ({
          devices: {
            ...state.devices,
            [deviceId]: {
              ...state.devices[deviceId],
              incidentStates,
            },
          },
        }));
      },

      updateDeviceChannel: (
        deviceId: string,
        channelId: string,
        value: any
      ) => {
        set((state) => {
          const device = state.devices[deviceId];
          if (!device) return state;

          const updatedChannels = { ...device.channels };
          for (const key of Object.keys(updatedChannels)) {
            const channel = updatedChannels[key];
            if (channel?.id === channelId) {
              updatedChannels[key] = {
                ...channel,
                current: { value, timestamp: new Date() },
              };
            }
          }

          return {
            devices: {
              ...state.devices,
              [deviceId]: {
                ...device,
                channels: updatedChannels,
              },
            },
            lastUpdated: { ...state.lastUpdated, operationState: Date.now() },
          };
        });
      },

      updateDeviceStateChannel: (
        deviceId: string,
        channelId: string,
        value: any
      ) => {
        set((state) => {
          const device = state.devices[deviceId];
          if (!device) return state;

          const updatedStateChannels = { ...device.stateChannels };
          for (const key of Object.keys(updatedStateChannels) as Array<
            keyof DeviceStateChannels
          >) {
            const channel = updatedStateChannels[key];
            if (channel?.id === channelId) {
              updatedStateChannels[key] = {
                ...channel,
                current: { value, timestamp: new Date() },
              };
            }
          }

          return {
            devices: {
              ...state.devices,
              [deviceId]: {
                ...device,
                stateChannels: updatedStateChannels,
              },
            },
            lastUpdated: { ...state.lastUpdated, onlineState: Date.now() },
          };
        });
      },

      updateDeviceChannels: (deviceId: string, channels: DeviceChannels) => {
        set((state) => {
          const device = state.devices[deviceId];
          if (!device) return state;

          return {
            devices: {
              ...state.devices,
              [deviceId]: {
                ...device,
                channels,
              },
            },
            lastUpdated: { ...state.lastUpdated, operationState: Date.now() },
          };
        });
      },

      updateDeviceStateChannels: (
        deviceId: string,
        stateChannels: DeviceStateChannels
      ) => {
        set((state) => {
          const device = state.devices[deviceId];
          if (!device) return state;

          return {
            devices: {
              ...state.devices,
              [deviceId]: {
                ...device,
                stateChannels,
              },
            },
            lastUpdated: { ...state.lastUpdated, onlineState: Date.now() },
          };
        });
      },

      setLoading: (
        key: keyof DevicesStore["loadingStates"],
        loading: boolean
      ) => {
        set((state) => ({
          loadingStates: { ...state.loadingStates, [key]: loading },
        }));
      },

      setError: (key: keyof DevicesStore["errors"], error: string | null) => {
        set((state) => ({
          errors: { ...state.errors, [key]: error },
        }));
      },

      clearErrors: () => {
        set({ errors: {} });
      },

      // Selectors
      getDevice: (id: string) => get().devices[id],

      getDevices: () => Object.values(get().devices),

      getDevicesByClass: (className: string) =>
        Object.values(get().devices).filter((d) => d.class === className),

      getDevicesByRoom: (roomId: string) =>
        Object.values(get().devices).filter((d) => d.location.room === roomId),

      getCalculatedDeviceState: (deviceId: string) => {
        const device = get().devices[deviceId];
        if (!device) return null;

        const status = mapOnlineState(
          device.stateChannels?.status?.current?.value as string
        );

        return { status } as DeviceState;
      },

      getCalculatedFumeHoodState: (deviceId: string) => {
        const device = get().devices[deviceId];
        if (!device) return null;

        // Calculate status
        let status = mapOnlineState(
          device.stateChannels?.status?.current?.value as string
        );

        // If fumehood, status also depends on connection between connectorbox and fumehood via can
        if (
          device.class === "workplace" &&
          device.stateChannels.canCommError?.current?.value === 1
        ) {
          status = "error";
        }

        // Calculate operation mode
        let operation: OperationMode | undefined;
        if (device.class === "workplace") {
          operation = device.channels?.operation?.current
            ?.value as OperationMode;
        }

        // Calculate messages
        const messages: FumeHoodStateMessage[] = [];
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.bootupChannel
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.lmb1Info
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.lmb2Info
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.serviceInfo
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.tempInfo
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.werkInfo
        );

        return { status, operation, messages };
      },

      getDeviceMessages: (deviceId: string) => {
        const device = get().devices[deviceId];
        if (!device) return [];

        const messages: FumeHoodStateMessage[] = [];
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.bootupChannel
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.lmb1Info
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.lmb2Info
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.serviceInfo
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.tempInfo
        );
        addFumehoodStateMessageFromChannel(
          messages,
          device.stateChannels.werkInfo
        );

        return messages;
      },

      getDeviceOperationMode: (deviceId: string) => {
        const device = get().devices[deviceId];
        if (!device || device.class !== "workplace") return undefined;

        return device.channels?.operation?.current?.value as OperationMode;
      },

      getDeviceStatus: (deviceId: string) => {
        const device = get().devices[deviceId];
        if (!device) return undefined;

        let status = mapOnlineState(
          device.stateChannels?.status?.current?.value as string
        );

        if (
          device.class === "workplace" &&
          device.stateChannels.canCommError?.current?.value === 1
        ) {
          status = "error";
        }

        return status;
      },
    })),
    {
      name: "devices-store", // Name that will appear in Redux DevTools
    }
  )
);
